// public/js/interaction/shared.js
//
// Shared utilities and state management for Oscilla Contribution modules
// (Annotations, Markers, Triggers, Recordings)
//
// This module provides:
// - Shared state object
// - Common utility functions (ulidLike, nowMs, etc.)
// - Annotation CRUD operations
// - WebSocket helpers
// - Storage helpers

import { projectName } from "../system/projectContext.js";
import { transport } from "../system/transportState.js";

// =============================================================
// CONSTANTS
// =============================================================

export const STORAGE_PREFIX = "oscilla_annotations_v1";
export const DEFAULT_AUTHOR_LABEL = "Performer";
export const POLL_SOCKET_MS = 500;

// =============================================================
// SHARED STATE
// =============================================================

export const state = {
    initialized: false,
    enabled: true,
    annotationMode: false,
    shareByDefault: false,

    project: null,
    items: [],

    // layers
    scoreLayer: null,
    pageLayer: null,

    // editor
    activeEditor: null,

    // socket polling
    socketPollId: null,
};

// Global flag for text input (prevents keyboard shortcuts)
window.oscillaTextInputActive = false;

// =============================================================
// UTILITY FUNCTIONS
// =============================================================

/**
 * Generate a unique ID (ULID-like)
 */
export function ulidLike() {
    return (
        "ann_" +
        Date.now().toString(36) +
        "_" +
        Math.random().toString(36).slice(2, 10)
    );
}

/**
 * Current timestamp in milliseconds
 */
export function nowMs() {
    return Date.now();
}

/**
 * Safe JSON parse with fallback
 */
export function safeJsonParse(str, fallback) {
    try {
        return JSON.parse(str);
    } catch {
        return fallback;
    }
}

/**
 * Clamp a number to 0-1 range
 */
export function clamp01(n) {
    if (typeof n !== "number" || !isFinite(n)) return 0;
    return Math.max(0, Math.min(1, n));
}

/**
 * Get current localScale computed fresh from SVG dimensions.
 * This ensures correct values even during resize before transport updates window.localScale.
 * Falls back to window.localScale if SVG dimensions aren't available.
 * @returns {number} The scale factor from world to screen coordinates
 */
export function getLocalScale() {
    const scoreContainer = document.getElementById("scoreContainer");
    const svg = scoreContainer?.querySelector("svg");
    const svgWidth = svg?.getBoundingClientRect().width || 0;
    const worldWidth = transport.scoreWidth || 1;

    if (svgWidth > 0 && worldWidth > 0) {
        return svgWidth / worldWidth;
    }

    // Fall back to cached value
    if (typeof window.localScale === "number" && isFinite(window.localScale) && window.localScale > 0) {
        return window.localScale;
    }

    return 1;
}

// =============================================================
// PROJECT & MODE CONTEXT
// =============================================================

/** The open project's name; annotations with no project are kept under "unknown_project". */
export function getProjectName() {
    return projectName("unknown_project");
}

/** This client's author id, kept in localStorage. */
export function getAuthorId() {
    let id = localStorage.getItem("oscilla_author_id");
    if (!id) {
        id = ulidLike();
        localStorage.setItem("oscilla_author_id", id);
    }
    return id;
}

export function getAuthorLabel() {
    return DEFAULT_AUTHOR_LABEL;
}

/**
 * Get current mode context (scroll vs page)
 */
export function getModeContext() {
    const mode = window.oscillaMode || "scroll";
    let pageId = null;

    if (mode === "page") {
        // Try to find the current visible page's identifier
        const pageContainer = document.getElementById("singlePage-container");
        if (pageContainer) {
            const svg = pageContainer.querySelector("svg");
            if (svg) {
                pageId = svg.id || svg.getAttribute("data-page-id") || null;
            }
        }
    }

    return { mode, pageId };
}

// =============================================================
// SERVER STORAGE
// =============================================================

/**
 * Load annotations from server (async)
 */
export async function loadFromServer(project) {
    try {
        const res = await fetch(`/api/annotations/${encodeURIComponent(project)}`);
        if (!res.ok) {
            console.warn("[annotations] Failed to load from server:", res.status);
            return [];
        }
        const data = await res.json();
        if (!data || !Array.isArray(data.items)) return [];
        return data.items;
    } catch (e) {
        console.warn("[annotations] Failed to load from server:", e);
        return [];
    }
}

/**
 * Save annotations to server (async, fire-and-forget)
 */
export function saveToServer(project, items) {
    const payload = JSON.stringify({ version: 1, savedAt: nowMs(), items });
    fetch(`/api/annotations/${encodeURIComponent(project)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload
    })
    .then(res => {
        if (!res.ok) console.warn("[annotations] Failed to save to server:", res.status);
    })
    .catch(e => console.warn("[annotations] Failed to save to server:", e));
}

// Legacy aliases for compatibility
export const loadLocal = loadFromServer;
export const saveLocal = saveToServer;

// =============================================================
// WEBSOCKET HELPERS
// =============================================================

/**
 * Get the active WebSocket connection
 */
function getWs() {
    return window.socket || null;
}

export function wsCanSend(ws) {
    const socket = ws || getWs();
    return socket && socket.readyState === WebSocket.OPEN;
}

export function wsSend(type, payload) {
    const ws = getWs();
    if (!wsCanSend(ws)) {
        // a public preview's guest has none, and knows it (socket.js)
        if (!window.oscillaPublicGuest) console.warn("[annotations] wsSend failed: WebSocket not connected");
        return;
    }

    try {
        ws.send(JSON.stringify({ type, ...payload }));
    } catch (e) {
        console.warn("[annotations] wsSend failed:", e);
    }
}

// =============================================================
// ANNOTATION CRUD OPERATIONS
// =============================================================

// Render callback - set by main module
let renderAllCallback = null;

export function setRenderCallback(callback) {
    renderAllCallback = callback;
}

function triggerRender() {
    if (typeof renderAllCallback === "function") {
        renderAllCallback();
    }
}

/**
 * Add a new annotation
 */
export function addAnnotation(item) {
    state.items.push(item);

    if (item.scope === "shared") {
        // Server persists via WebSocket - don't also HTTP POST
        wsSend("annotation_add", { project: state.project, item });
    } else {
        // Local-only: save via HTTP
        saveLocal(state.project, state.items);
    }

    triggerRender();
}

/**
 * Update an existing annotation
 * @param {string} id - Annotation ID
 * @param {object} patch - Updates to apply
 * @param {object} [opts] - Options
 * @param {boolean} [opts.skipRender] - Skip full re-render (for position-only updates)
 */
export function updateAnnotation(id, patch, opts = {}) {
    const idx = state.items.findIndex((x) => x.id === id);
    if (idx < 0) return;

    const prev = state.items[idx];
    const next = {
        ...prev,
        ...patch,
        updatedAt: nowMs(),
    };

    // if scope changes, reflect it
    if (patch.scope) next.scope = patch.scope;

    state.items[idx] = next;

    if (next.scope === "shared") {
        // Server persists via WebSocket - don't also HTTP POST
        wsSend("annotation_update", { project: state.project, item: next });
    } else {
        // Local-only: save via HTTP
        saveLocal(state.project, state.items);
        // If it was shared and now local: tell server to delete
        if (prev.scope === "shared") {
            wsSend("annotation_delete", { project: state.project, id: next.id });
        }
    }

    if (!opts.skipRender) {
        triggerRender();
    }
}

/**
 * Delete an annotation
 */
export function deleteAnnotation(id) {
    const idx = state.items.findIndex((x) => x.id === id);
    if (idx < 0) return;
    const prev = state.items[idx];
    state.items.splice(idx, 1);

    if (prev.scope === "shared") {
        // Server persists via WebSocket - don't also HTTP POST
        wsSend("annotation_delete", { project: state.project, id });
    } else {
        // Local-only annotations: save via HTTP
        saveLocal(state.project, state.items);
    }

    triggerRender();
}

// =============================================================
// ANNOTATION SELECTION STATE
// =============================================================

const selectedAnnotationIds = new Set();

export function getSelectedAnnotations() {
    return selectedAnnotationIds;
}

export function selectAnnotation(id, additive = false) {
    if (!additive) selectedAnnotationIds.clear();
    selectedAnnotationIds.add(id);
}

export function deselectAnnotation(id) {
    selectedAnnotationIds.delete(id);
}

export function clearAnnotationSelection() {
    selectedAnnotationIds.clear();
}

export function isAnnotationSelected(id) {
    return selectedAnnotationIds.has(id);
}

export function toggleAnnotationSelection(id) {
    if (selectedAnnotationIds.has(id)) {
        selectedAnnotationIds.delete(id);
    } else {
        selectedAnnotationIds.add(id);
    }
}

// =============================================================
// LAYER HELPERS
// =============================================================

/**
 * Clear pins from a layer
 */
export function clearPins(layer) {
    if (!layer) return;
    layer.querySelectorAll(".osc-anno-pin").forEach((n) => n.remove());
}

/**
 * Clear markers from a layer
 */
export function clearMarkers(layer) {
    if (!layer) return;
    layer.querySelectorAll(".osc-marker").forEach((n) => n.remove());
}

/**
 * Check if an item should be rendered based on current mode
 */
export function shouldRenderItem(item) {
    if (!state.enabled) return false;

    const { mode, pageId } = getModeContext();
    const a = item.anchor || {};

    if (mode === "page") {
        return a.mode === "page" && a.pageId && a.pageId === pageId;
    }

    // scroll mode
    return a.mode === "scroll";
}

// =============================================================
// EXPORTS
// =============================================================

export default {
    // Constants
    STORAGE_PREFIX,
    DEFAULT_AUTHOR_LABEL,
    POLL_SOCKET_MS,
    
    // State
    state,
    
    // Utilities
    ulidLike,
    nowMs,
    safeJsonParse,
    clamp01,
    
    // Project/Mode
    getProjectName,
    getModeContext,
    
    // Storage
    loadLocal,
    saveLocal,
    
    // WebSocket
    wsCanSend,
    wsSend,
    
    // CRUD
    setRenderCallback,
    addAnnotation,
    updateAnnotation,
    deleteAnnotation,
    
    // Selection
    getSelectedAnnotations,
    selectAnnotation,
    deselectAnnotation,
    clearAnnotationSelection,
    isAnnotationSelected,
    toggleAnnotationSelection,

    // Layer helpers
    clearPins,
    clearMarkers,
    shouldRenderItem,
};
